import React, { useState } from 'react';
import { borderRadius } from '../../Utilities/utils/Theming';
import { useStyletron } from '../../Themes';
import { Button } from '../Button';
import { MIcon, NoIcon } from '../Icon';
import { Popover, StatefulPopover } from '../Popover';
import { ListItemStyle } from '../List/styles';
import { TItem } from '../List/types';
import { focusRingInset, focusRingNoGap } from '../../Themes/base/focusRing';
import * as meta from '../../meta-data.json';
import { UncontrolledPopover } from '../Popover/view';

type TUtilClassname = {
	className: any;
	props: any;
	disabled: any;
	isHighlighted: any;
	subItemContent: any;
	selected: any;
	theme: any;
	size: any;
	css?: any;
}

const utilClassName = ({ className, props, disabled, isHighlighted, subItemContent, selected, theme, size }: TUtilClassname) => {
	const _hoverDisabled = disabled ? { borderColor: "transparent" } : {}

	return {
		...ListItemStyle({
			...props,
			...{
				$disabled: disabled,
				$isHighlighted: isHighlighted,
				$selected: subItemContent ? false : selected,
				$theme: theme,
				size: size
			}
		}),
		cursor: disabled ? 'default' : 'pointer',
		':focus': {
			border: '1px solid var(--epods--color-border-active)',
			..._hoverDisabled,
			backgroundColor: 'transparent',
			outline: 'none',
		}
	}
}

const utilEnhancer = (item: any, css: any) => {
	if (typeof item.icon == 'string') {
		return (
			<MIcon
				icon={item.icon}
				size="var(--epods--icon-size-small)"
				color="var(--epods--color-text-primary)"
				title={item.icon}
				className={css({
					marginRight: 'var(--epods--space-inset-s)',
				})}
			/>
		);
	} else if (typeof item.icon == 'boolean' && item.icon === false) {
		return (
			<NoIcon
				className={css({
					marginRight: 'var(--epods--space-inset-s)',
				})}
			/>
		);
	} else {
		return item.icon
	}
}

export const BasicItemOption = (item: TItem) => {
	const [css] = useStyletron();
	const enhancer = utilEnhancer(item, css);

	return (
		<div id={String(item?.id)} className={css({
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			width: '100%',
		})} >
			<div className={css({
				display: 'flex',
				alignItems: 'center',
				overflow: 'hidden',
				width: '100%',
			})} >
				{enhancer}
				<div className={css((typeof item.label === 'string')
					? {
						textOverflow: 'ellipsis',
						overflow: 'hidden',
						whiteSpace: 'nowrap',
						...borderRadius('var(--epods--border-radius)'),
					} : {})} >
					{item.label}
				</div>
			</div>
		</div>
	);
};

export const BasicItem = (props: any) => {
	const {
		selected,
		isHighlighted,
		disabled,
		theme,
		size,
		className,
		label,
		icon,
		id,
		checked,
		custom,
		subItemContent,
		subItemPosition,
		subListItemContent,
		subListItemPosition,
		onFocus,
		onBlur,
		...rest } = props;

	const [css] = useStyletron();
	const [ariaSelected, setAriaSelected] = useState<any>("false");
	const _role = disabled ? {} : { role: "button", tabIndex: 0 }
	return (
		<div {..._role}
			className={`${className} ${css(utilClassName({ className, props, disabled, isHighlighted, subItemContent, selected, theme, size }))}`}
			onFocus={(e) => { setAriaSelected(true); onFocus && onFocus(e) }}
			onBlur={(e) => { setAriaSelected(false); onBlur && onBlur(e) }}
			aria-disabled={disabled ? "true" : "false"}
			data-epo={`epo-item-${meta.version}`}
			part={`epo-item-${meta.version}`}
			{...rest}
		>

			<BasicItemOption
				label={label}
				icon={icon}
				id={id}
				disabled={disabled}
				selected={selected}
				size={size}
				checked={checked}
				custom={custom}
				//onClick={(e: any) => { onClick && onClick(e); }}
				subItemContent={subItemContent}
				subItemPosition={subItemPosition} />
		</div>
	)
}

export const SplitItemOption = (item: TItem) => {
	const [css] = useStyletron();
	const enhancer = utilEnhancer(item, css);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const _selectedBg = item.subItemContent && item?.selected ? {
		marginTop: 'var(--epods--space-inset-negative-xs)',
		marginBottom: 'var(--epods--space-inset-negative-xs)',
		marginLeft: 'var(--epods--space-inset-negative-s)',
		//marginRight: 'var(--epods--space-inset-negative-s)',
		paddingTop: 'var(--epods--space-inset-xs)',
		paddingBottom: 'var(--epods--space-inset-xs)',
		paddingLeft: 'var(--epods--space-inset-s)',
		paddingRight: 'var(--epods--space-inset-s)',
		...borderRadius('var(--epods--border-radius)'),
	} : {}


	return (
		<div id={String(item?.id)} className={css({
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			width: '100%',
		})} >
			<div className={css({
				display: 'flex',
				alignItems: 'center',
				overflow: 'hidden',
				width: '100%',
				..._selectedBg,
			})} >
				<div className={css({
					..._selectedBg,
					flexGrow: 1,
					backgroundColor: item?.selected && !!!item?.disabled ? 'var(--epods--color-bg-active)' : 'inherit',
					width: '100%',
					display: 'flex',
					marginRight: 'var(--epods--space-inset-l)'
				})}>
					{enhancer}
					<div className={css((typeof item.label === 'string')
						? {
							textOverflow: 'ellipsis',
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							...borderRadius('var(--epods--border-radius)'),
						} : {})} >
						{item.label}
					</div>
				</div>
			</div>
		</div >
	);
};

export const SplitItem = (props: any) => {
	const {
		selected,
		isHighlighted,
		disabled,
		theme,
		size,
		className,
		label,
		icon,
		id,
		checked,
		custom,
		subItemContent,
		subItemPosition,
		subListItemContent,
		subListItemPosition,
		onFocus,
		onBlur,
		...rest } = props;
	const [css] = useStyletron();
	const [ariaSelected, setAriaSelected] = useState<any>("false");
	const _role = disabled ? {} : { role: "button", tabIndex: 0 };
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const _focusR = (size == "compact" || size == "mini") ? focusRingInset : focusRingNoGap
	const spanRef = React.useRef<any>(null);
	return (
		<div className={css({ position: 'relative' })}
			{...{
				'data-epo': `epo-item-${meta.version}`,
				part: `epo-item-${meta.version}`
			}}>
			<div {..._role}
				className={`${className} ${css(utilClassName({ className, props, disabled, isHighlighted, subItemContent, selected, theme, size }))} ${css({
					paddingTop: 'var(--epods--space-inset-xxs)',
					paddingBottom: 'var(--epods--space-inset-xxs)',
					overflow: 'hidden'
				})}`}
				onFocus={(e) => { setAriaSelected(true); onFocus && onFocus(e) }}
				onBlur={(e) => { setAriaSelected(false); onBlur && onBlur(e) }}
				aria-disabled={disabled ? "true" : "false"}
				{...rest}
			>

				<SplitItemOption
					label={label}
					icon={icon}
					id={id}
					disabled={disabled}
					selected={selected}
					size={size}
					checked={checked}
					custom={custom}
					//onClick={(e: any) => { onClick && onClick(e); }}
					subItemContent={subItemContent}
					subItemPosition={subItemPosition} />
			</div>

			<div onClick={(e) => { e.stopPropagation() }}
				className={css({
					position: 'absolute',
					top: '0',
					right: 'calc(var(--epods--space-inset-xs) + var(--epods--space-inset-s))',
					height: `var(--epods--list-item-${size || "default"}-height)`,
					display: 'flex',
					alignItems: 'center',
				})}>
				<Popover
					isOpen={isOpen}
					inverted
					content={subItemContent}
					placement={subItemPosition}
					showArrow={false}
					className={'epo-item-option ' + css({ '[aria-expanded="true"]': { backgroundColor: 'red' } })}
					onClick={() => setIsOpen(!isOpen)}
					autoFocus
					onClickOutside={() => setIsOpen(false)}
					onEsc={() => setIsOpen(false)}
					mountNode={spanRef.current}
					overrides={{
						Body: {
							style: {
								marginLeft: 'var(--epods--space-inset-m)'
							}
						},
						Inner: {
							props: {
								onClick: (e: any) => e.stopPropagation()
							}
						}
					}} >
					<span>
						<Button iconBefore="navigate_next" theme="minimal" size={size == "large" ? "compact" : "mini"} shape='last' isSelected={isOpen}
							aria-label="Open subpanel"
							disabled={disabled}
							className={css({
								borderLeft: "1px solid var(--epods--color-border-light) !important",
								paddingLeft: "var(--epods--space-inset-s) !important",
								paddingRight: "var(--epods--space-inset-s) !important",
								minWidth: "auto !important",
								':focus': {
									..._focusR
								}
							})} />
					</span>
				</Popover>
				<span ref={spanRef}></span>
			</div>
		</div >
	)
}

export const SubListItemOption = (item: TItem) => {
	const [css] = useStyletron();
	const enhancer = utilEnhancer(item, css);

	return (
		<div id={String(item?.id)} className={css({
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			width: '100%',
		})} >
			<div className={css({
				display: 'flex',
				alignItems: 'center',
				overflow: 'hidden',
				width: '100%',
			})} >
				{enhancer}

				<div className={css((typeof item.label === 'string')
					? {
						textOverflow: 'ellipsis',
						overflow: 'hidden',
						whiteSpace: 'nowrap',
						...borderRadius('var(--epods--border-radius)'),
					} : {})}
				>
					{item.label}
				</div>
			</div>
			<MIcon icon="navigate_next" size="small" title="navigate_next"
				className={css({ marginLeft: "var(--epods--space-inset-s)", marginRight: "var(--epods--space-inset-s)" })} />
		</div >
	);
};

export const SubListItem = (props: any) => {
	const {
		selected,
		isHighlighted,
		disabled,
		theme,
		size,
		className,
		label,
		icon,
		id,
		checked,
		custom,
		subItemContent,
		subItemPosition,
		subListItemContent,
		subListItemPosition,
		onFocus,
		onBlur,
		...rest } = props;
	const [css] = useStyletron();
	const [ariaSelected, setAriaSelected] = useState<any>("false");
	const [_visited, setVisited] = useState<any>(false);
	const _role = disabled ? {} : { role: "button", tabIndex: 0 }
	const spanRef = React.useRef<any>(null);
	return (
		<>
			{disabled
				?
				<div {..._role}
					className={`${className} ${css(utilClassName({ className, props, disabled, isHighlighted: _visited, subItemContent, selected, theme, size }))}`}
					onFocus={(e) => { setAriaSelected(true); onFocus && onFocus(e) }}
					onBlur={(e) => { setAriaSelected(false); onBlur && onBlur(e) }}
					aria-disabled={disabled ? "true" : "false"}
					data-epo={`epo-item-${meta.version}`}
					part={`epo-item-${meta.version}`}
					{...rest}
				>

					<SubListItemOption
						label={label}
						icon={icon}
						id={id}
						disabled={disabled}
						size={size}
						selected={selected}
						checked={checked}
						custom={custom}
						visited={_visited}
						subItemPosition={subItemPosition} />
				</div>
				:
				<UncontrolledPopover
					inverted
					content={subListItemContent}
					placement={subListItemPosition}
					showArrow={false}
					//className={'epo-item-option ' /* + css({ '[aria-expanded="true"]': { backgroundColor: 'red' } }) */}
					autoFocus
					triggerType="hover"
					onChange={(a: boolean) => setVisited(a)}
					//mountNode={spanRef.current}
					overrides={{
						Inner: {
							props: {
								onClick: (e: any) => e.stopPropagation()
							}
						}
					}}
				>
					<div {..._role}
						className={`${className} ${css(utilClassName({ className, props, disabled, isHighlighted: _visited, subItemContent, selected, theme, size, css }))}`}
						onFocus={(e) => { setAriaSelected(true); onFocus && onFocus(e) }}
						onBlur={(e) => { setAriaSelected(false); onBlur && onBlur(e) }}
						aria-disabled={disabled ? "true" : "false"}
						data-epo={`epo-item-${meta.version}`}
						part={`epo-item-${meta.version}`}
						{...rest}
					>

						<SubListItemOption
							label={label}
							icon={icon}
							id={id}
							disabled={disabled}
							size={size}
							selected={selected}
							checked={checked}
							custom={custom}
							visited={_visited}
							subItemPosition={subItemPosition} />
					</div>
				</UncontrolledPopover>
			}
			{/* <span ref={spanRef}></span> */}
		</>
	)
}
